import { useCallback, useState } from 'react';

import { useTranslate } from '../../../../../i18n/useTranslationModules';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import { QuizzesStatus, Survey, SurveyQuestion, SurveyType } from '../../../../../types/working-model';
import { setStorageItem } from '../../../../../utils/local-storage';
import { getPollAnswerLocalStorageKey, getQuizRetakesLocalStorageKey, getStoredQuizRetakes } from '../../session-modules/survey/survey';
import { getSurveyText } from '../utils/engage.utils';

type UseGetAnsweredQuestionCount = (survey?: Survey) => number;

export const useGetAnsweredQuestionCount: UseGetAnsweredQuestionCount = (survey) => {
	const eventId = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.event);

	if (!survey) return 0;

	const { questions, survey: surveyId } = survey;

	const answeredQuestions: string[] = [];

	questions.forEach((question: SurveyQuestion) => {
		if (!eventId) return;

		const lsKey = getPollAnswerLocalStorageKey(question, surveyId, eventId);
		const getStoredAnswers = localStorage.getItem(lsKey);

		if (getStoredAnswers) {
			answeredQuestions.push(getStoredAnswers);
		}
	});

	return answeredQuestions.length;
};

export type TakeCount = { current: number; total: number } | null;

type UseQuizRetakes = (survey: Survey, preview?: boolean) => {
	canRetake: boolean;
	revealCorrect: boolean;
	takeCount: TakeCount;
	recordRetake: VoidFunction;
};

export const useQuizRetakes: UseQuizRetakes = (survey, preview) => {
	const eventId = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.event);
	const { survey: surveyId, allow_retake, max_attempts, survey_type, show_correct_answer, show_attempt_number } = survey;

	const storageKey = eventId && !preview && survey_type === SurveyType.quiz
		? getQuizRetakesLocalStorageKey(surveyId, eventId)
		: null;

	const [retakesUsed, setRetakesUsed] = useState(() => {
		if (!storageKey || !eventId) return 0;

		return getStoredQuizRetakes(surveyId, eventId);
	});

	const recordRetake = useCallback(() => {
		const next = retakesUsed + 1;

		if (storageKey) {
			setStorageItem(storageKey, String(next));
		}

		setRetakesUsed(next);
	}, [retakesUsed, storageKey]);

	const canRetake = allow_retake && retakesUsed + 1 < (max_attempts ?? Infinity);
	const totalTakes = show_attempt_number && allow_retake && max_attempts ? max_attempts : 0;

	return {
		canRetake,
		revealCorrect: show_correct_answer !== false && !canRetake,
		takeCount: totalTakes ? { current: Math.min(retakesUsed + 1, totalTakes), total: totalTakes } : null,
		recordRetake,
	};
};

type UseGetEndScreenDescription = (params: {
	quizStatus: QuizzesStatus;
	isQuizType: boolean;
	maxQuestions: number;
	preview?: boolean;
}) => string;

export const useGetEndScreenDescription: UseGetEndScreenDescription = ({
	quizStatus,
	isQuizType,
	maxQuestions,
	preview,
}) => {
	const { t } = useTranslate("session");

	if (isQuizType && preview) {
		return getSurveyText({
			textToReplace: t('session:QuizCorrectAnsweredCount'),
			replaceMapping: {
				'1': maxQuestions,
				'0': maxQuestions,
			},
		});
	}

	if (isQuizType && quizStatus) {
		const { correctAnswers, totalAnswered } = quizStatus;
		const calculatedPercentage = Math.round((correctAnswers / totalAnswered) * 100);

		return getSurveyText({
			textToReplace: t('session:QuizCorrectAnsweredCount'),
			replaceMapping: {
				'1': correctAnswers,
				'0': totalAnswered,
				'100': calculatedPercentage,
			},
		});
	}

	return t('session:We truly value the information you have provided', 'We truly value the information you have provided');
};

export const useGetHoveredStateButtonName = (isQuestionType: boolean, surveyCompleted: boolean, isCommentBoxType: boolean) => {
	const { t } = useTranslate("session");

	if (isQuestionType) {
		return t('Ask question', 'Ask question');
	}

	if (isCommentBoxType) {
		return t('Post a comment', 'Post a comment');
	}

	if (surveyCompleted) {
		return t('View results', 'View results');
	}

	return t('Answer', 'Answer');
};
